import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useRestaurant } from "../../../contexts/RestaurantContext";
import { formatPriceWithSpace } from "../../../utils/priceFormatter";
import "./OnlineOrderingPromo.css";

const APP_PATHS = ["/menu", "/deals", "/combos", "/home", "/checkout", "/cart"];
const AUTO_MS = 6000;

function resolvePromoHref(linkUrl, restaurantSlug) {
  const raw = String(linkUrl || "").trim();
  if (!raw) return null;

  if (/^https?:\/\//i.test(raw)) {
    return { type: "external", href: raw };
  }

  const path = raw.startsWith("/") ? raw : `/${raw}`;
  const lower = path.toLowerCase();
  const known = APP_PATHS.find((p) => lower === p || lower.startsWith(`${p}/`));
  if (restaurantSlug && (known || path.startsWith("/"))) {
    return { type: "internal", href: `/${restaurantSlug}${path}` };
  }
  return { type: "internal", href: path };
}

function offerAudienceLabel(offer) {
  const threshold = Number(offer.orderCountThreshold) || 0;
  const compare = String(offer.orderCountCompareType || "Equals");
  const atMost = compare.toLowerCase() === "atmost";
  if (threshold === 0) {
    return atMost ? "New customers" : "First order";
  }
  if (atMost) {
    return threshold === 1 ? "First 2 orders" : `First ${threshold + 1} orders`;
  }
  if (threshold === 1) return "Your 2nd order";
  return `Order #${threshold + 1}`;
}

function offerHint(offer, currency) {
  const parts = [];
  if (offer.isSetMinimumSpend && offer.minimumSpendAmount > 0) {
    parts.push(`Min spend ${formatPriceWithSpace(offer.minimumSpendAmount, currency)}`);
  }
  if (offer.isSetMaxDiscountValue && offer.maxDiscountValue > 0) {
    parts.push(`Up to ${formatPriceWithSpace(offer.maxDiscountValue, currency)}`);
  }
  parts.push("Auto-applied at checkout");
  return parts.join(" · ");
}

function formatPct(value) {
  const n = Number(value) || 0;
  return n % 1 === 0 ? String(Math.round(n)) : n.toFixed(1);
}

function wrapIndex(i, length) {
  if (length <= 0) return 0;
  return ((i % length) + length) % length;
}

export default function OnlineOrderingPromo({
  banners = [],
  offers = [],
  variant = "strip",
  currency = "£",
}) {
  const navigate = useNavigate();
  const { restaurantSlug } = useRestaurant();
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const touchStartX = useRef(null);
  const reduceMotion = useRef(
    typeof window !== "undefined" &&
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );

  const imageSlides = useMemo(
    () => (Array.isArray(banners) ? banners.filter((b) => b?.imageUrl) : []),
    [banners]
  );
  const primaryOffer = useMemo(() => {
    if (!Array.isArray(offers) || offers.length === 0) return null;
    return [...offers].sort((a, b) => b.discountPercentage - a.discountPercentage)[0];
  }, [offers]);

  const hasImages = imageSlides.length > 0;
  const hasOffer = Boolean(primaryOffer);
  const isHero = variant === "hero";

  useEffect(() => {
    setIndex(0);
  }, [imageSlides.length]);

  const goTo = useCallback(
    (next) => {
      if (imageSlides.length <= 1) return;
      setIndex(wrapIndex(next, imageSlides.length));
    },
    [imageSlides.length]
  );

  useEffect(() => {
    if (!hasImages || imageSlides.length < 2 || paused || reduceMotion.current) return undefined;
    const timer = window.setInterval(() => goTo(index + 1), AUTO_MS);
    return () => window.clearInterval(timer);
  }, [hasImages, imageSlides.length, paused, index, goTo]);

  const openHref = (linkUrl, fallbackPath = "/menu") => {
    const resolved =
      resolvePromoHref(linkUrl, restaurantSlug) ||
      resolvePromoHref(fallbackPath, restaurantSlug);
    if (!resolved) return;
    if (resolved.type === "external") {
      window.open(resolved.href, "_blank", "noopener,noreferrer");
      return;
    }
    navigate(resolved.href);
  };

  if (!hasImages && !hasOffer) return null;

  const current = hasImages ? imageSlides[index] : null;
  const slideTitle = String(current?.title || "").trim();
  const slideDesc = String(current?.description || current?.subtitle || "").trim();

  return (
    <section
      className={`oo-promo oo-promo--${variant}`}
      aria-label="Offers and promotions"
    >
      <div className="oo-promo__inner">
        {hasImages ? (
          <div
            className={`oo-promo-showcase${isHero ? " oo-promo-showcase--hero" : ""}`}
            onMouseEnter={() => setPaused(true)}
            onMouseLeave={() => setPaused(false)}
            onFocus={() => setPaused(true)}
            onBlur={() => setPaused(false)}
            onTouchStart={(e) => {
              touchStartX.current = e.changedTouches[0]?.clientX ?? null;
            }}
            onTouchEnd={(e) => {
              const start = touchStartX.current;
              const end = e.changedTouches[0]?.clientX;
              touchStartX.current = null;
              if (start == null || end == null || imageSlides.length < 2) return;
              const delta = end - start;
              if (Math.abs(delta) < 40) return;
              goTo(delta < 0 ? index + 1 : index - 1);
            }}
          >
            <div className="oo-promo-showcase__frame">
              <button
                type="button"
                className="oo-promo-showcase__stage"
                onClick={() => openHref(current.linkUrl || "/menu")}
                aria-label={slideTitle || "View promotion"}
              >
                {imageSlides.map((slide, i) => (
                  <img
                    key={slide.bannerId || slide.imageUrl || i}
                    src={slide.imageUrl}
                    alt={slide.title || "Promotion"}
                    className={`oo-promo-showcase__img${i === index ? " is-active" : ""}`}
                    draggable={false}
                    onError={(e) => {
                      e.currentTarget.style.visibility = "hidden";
                    }}
                  />
                ))}
                <span
                  className={`oo-promo-showcase__veil${!slideTitle ? " oo-promo-showcase__veil--light" : ""}`}
                  aria-hidden
                />
                <span className="oo-promo-showcase__glow" aria-hidden />

                {/* Only banner Title/desc from backoffice — never auto discount name/%/CTA */}
                {slideTitle ? (
                  <span className="oo-promo-showcase__copy">
                    <span className="oo-promo-showcase__title">{slideTitle}</span>
                    {slideDesc ? (
                      <span className="oo-promo-showcase__desc">{slideDesc}</span>
                    ) : null}
                  </span>
                ) : null}
              </button>

              {imageSlides.length > 1 ? (
                <>
                  <button
                    type="button"
                    className="oo-promo-showcase__nav oo-promo-showcase__nav--prev"
                    aria-label="Previous banner"
                    onClick={(e) => {
                      e.stopPropagation();
                      goTo(index - 1);
                    }}
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
                      <path
                        d="M15 6 9 12l6 6"
                        stroke="currentColor"
                        strokeWidth="2.2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>
                  <button
                    type="button"
                    className="oo-promo-showcase__nav oo-promo-showcase__nav--next"
                    aria-label="Next banner"
                    onClick={(e) => {
                      e.stopPropagation();
                      goTo(index + 1);
                    }}
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
                      <path
                        d="m9 6 6 6-6 6"
                        stroke="currentColor"
                        strokeWidth="2.2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>
                  <div className="oo-promo-showcase__pager" role="tablist" aria-label="Banner slides">
                    {imageSlides.map((slide, i) => (
                      <button
                        key={slide.bannerId || i}
                        type="button"
                        role="tab"
                        aria-selected={i === index}
                        aria-label={`Banner ${i + 1}`}
                        className={`oo-promo-showcase__dot${i === index ? " is-active" : ""}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          setIndex(i);
                        }}
                      />
                    ))}
                  </div>
                </>
              ) : null}
            </div>
          </div>
        ) : null}

        {hasOffer && !hasImages ? (
          <button
            type="button"
            className="oo-promo-offer"
            onClick={() => openHref("", "/menu")}
          >
            <span className="oo-promo-offer__badge" aria-hidden>
              {formatPct(primaryOffer.discountPercentage)}% off
            </span>
            <span className="oo-promo-offer__copy">
              <span className="oo-promo-offer__title">
                {primaryOffer.discountName || `${offerAudienceLabel(primaryOffer)} discount`}
              </span>
              <span className="oo-promo-offer__meta">
                {offerAudienceLabel(primaryOffer)} · {offerHint(primaryOffer, currency)}
              </span>
            </span>
            <span className="oo-promo-offer__cta">Order now</span>
          </button>
        ) : null}
      </div>
    </section>
  );
}
